import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Sparkles, Download, Copy, Share2, Loader2, PartyPopper } from "lucide-react";

export default function PublicReel() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [reel, setReel] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    document.title = "Reveal reel — Unveil";
    api.get(`/public/events/${code}/highlight`)
      .then((r) => { setReel(r.data); setState("ready"); })
      .catch(() => setState("missing"));
  }, [code]);

  const shareUrl = `${window.location.origin}/api/reel/${code}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Share link copied!"); }
    catch { toast.error("Couldn't copy the link"); }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: reel?.title || "Our reveal reel", text: "Watch everyone's reactions!", url: shareUrl }); }
      catch { /* user cancelled */ }
    } else { copy(); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="public-reel-page">
      <header className="glass">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 font-display font-black text-xl bg-transparent border-0 cursor-pointer">
            <Sparkles className="w-5 h-5 text-gold" /> Unveil
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-12">
        {state === "loading" && <div className="flex items-center justify-center py-24 text-foreground/50"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading reel…</div>}

        {state === "missing" && (
          <div className="text-center py-24">
            <PartyPopper className="w-12 h-12 text-gold mx-auto mb-4" />
            <h1 className="font-display font-black text-3xl mb-2">Reel not ready</h1>
            <p className="text-foreground/60 mb-6">This reveal doesn't have a highlight reel yet.</p>
            <Button onClick={() => navigate("/")} className="rounded-full bg-gold hover:bg-gold/90 text-black font-semibold h-11 px-6">Explore Unveil</Button>
          </div>
        )}

        {state === "ready" && reel && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-xs tracking-[0.3em] uppercase text-gold font-bold mb-2">Highlight reel</p>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-2" data-testid="reel-title">{reel.title}</h1>
            {reel.host_name && <p className="text-foreground/60 mb-8">Hosted by {reel.host_name}</p>}
            <div className="rounded-3xl overflow-hidden border border-white/10 bg-black shadow-2xl">
              <video data-testid="reel-video" src={reel.highlight_url} poster={reel.poster_url || undefined} controls playsInline autoPlay muted className="w-full max-h-[70vh] bg-black" />
            </div>
            <div className="flex flex-wrap gap-3 mt-6 justify-center">
              <Button data-testid="reel-share-btn" onClick={share} className="rounded-full bg-gold hover:bg-gold/90 text-black font-semibold h-12 px-6"><Share2 className="w-4 h-4 mr-2" /> Share reel</Button>
              <a data-testid="reel-download-btn" href={reel.highlight_url} target="_blank" rel="noopener noreferrer" download className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-white text-black font-semibold hover:scale-105 transition-transform"><Download className="w-4 h-4" /> Download</a>
              <Button data-testid="reel-copy-btn" onClick={copy} variant="outline" className="rounded-full border-white/20 bg-transparent h-12 px-6"><Copy className="w-4 h-4 mr-2" /> Copy link</Button>
            </div>
            <p className="text-center text-sm text-foreground/40 mt-8">Made with <span className="text-gold">Unveil</span> — live reveal celebrations.</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
